import { useQuery } from '@tanstack/react-query';
import { UserPathApi } from 'src/api';
import { SessionSourceType } from 'src/api/learningSessions/types';
import { UserPathBlob, UserPathBlobsResponse } from 'src/api/userPath/types';
import { BLOB_PATH_PAGE_SIZE } from 'src/apps/Path/BlobPathComponent/useBlobPath';

type CompletionOptions = {
  languagePairId: number | null;
  sessionId: string;
  sessionSourceType: SessionSourceType;
  sessionSourceId: number;
  enabled: boolean;
  hasFinished: boolean;
};

const matchesSource = (blob: UserPathBlob, sourceType: SessionSourceType, sourceId: number) =>
  sourceType === 'language_pair' ||
  (sourceType === 'user_path_blob' ? blob.blobId === sourceId : blob.scenarioId === sourceId);

const getSessionPathPage = async (
  languagePairId: number,
  sourceType: SessionSourceType,
  sourceId: number,
): Promise<UserPathBlobsResponse> => {
  const currentPage = await UserPathApi.getBlobs(languagePairId, { limit: BLOB_PATH_PAGE_SIZE });
  if (currentPage.blobs.some(blob => matchesSource(blob, sourceType, sourceId))) {
    return currentPage;
  }

  // A list launched directly can be outside the current path page.
  if (sourceType !== 'language_pair') {
    for (let offset = 0; offset < currentPage.totalNumber; offset += BLOB_PATH_PAGE_SIZE) {
      if (offset === currentPage.offset) continue;
      const page = await UserPathApi.getBlobs(languagePairId, {
        offset,
        limit: BLOB_PATH_PAGE_SIZE,
      });
      if (page.blobs.some(blob => matchesSource(blob, sourceType, sourceId))) return page;
    }
  }

  return currentPage;
};

export const useCompletedBlobWordlists = ({
  languagePairId,
  sessionId,
  sessionSourceType,
  sessionSourceId,
  enabled,
  hasFinished,
}: CompletionOptions): UserPathBlob[] => {
  const before = useQuery({
    queryKey: [
      'blobSessionWordlists',
      'before',
      languagePairId,
      sessionId,
      sessionSourceType,
      sessionSourceId,
    ],
    queryFn: () => getSessionPathPage(languagePairId as number, sessionSourceType, sessionSourceId),
    enabled: enabled && languagePairId !== null && !hasFinished,
    staleTime: Infinity,
    retry: false,
  });
  const offset = before.data?.offset;
  const after = useQuery({
    queryKey: [
      'blobSessionWordlists',
      'after',
      languagePairId,
      sessionId,
      sessionSourceType,
      sessionSourceId,
      offset,
    ],
    // Keep the original offset: completion can move the current list onto another page.
    queryFn: () =>
      UserPathApi.getBlobs(languagePairId as number, {
        offset,
        limit: BLOB_PATH_PAGE_SIZE,
      }),
    enabled: enabled && languagePairId !== null && hasFinished && !!before.data,
    staleTime: Infinity,
    retry: false,
  });

  const beforePage = before.data;
  if (!enabled || !hasFinished || !beforePage || !after.data) return [];

  return after.data.blobs.filter(
    blob =>
      blob.status === 'completed' &&
      blob.numberOfLearnables > 0 &&
      matchesSource(blob, sessionSourceType, sessionSourceId) &&
      beforePage.blobs.some(
        previous =>
          previous.blobId === blob.blobId &&
          (previous.status === 'current' || previous.status === 'upcoming'),
      ),
  );
};
